import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    CommandDialog,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from "@/components/ui/command";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
    Briefcase, UsersThree, Buildings, CalendarBlank, ChartBar,
    ChartLineUp, ChartPieSlice, Bell, Gift, ChatCircleDots,
    ShieldCheck, CreditCard, Gear, SquaresFour, MagnifyingGlass,
    Plus, ArrowRight,
} from "@phosphor-icons/react";

/**
 * Global ⌘K / Ctrl+K command palette.
 *
 * Sections:
 *   - Navigation (role-scoped)
 *   - Quick actions ("New requirement", "New candidate", "Log out")
 *   - Search results (jobs · candidates · clients — debounced 200ms)
 *
 * Search endpoints reused as-is:
 *   - GET /jobs?q=...            (filtered client-side by role scope)
 *   - GET /candidates/search?q=  (already recruiter-scoped)
 *   - GET /clients?q=...
 *
 * Never touches localStorage, never persists — pure ephemeral overlay.
 */
const NAV_BY_ROLE = {
    admin: [
        ["/dashboard", "Dashboard", SquaresFour],
        ["/clients", "Clients", Buildings],
        ["/requirements", "Requirements", Briefcase],
        ["/candidates", "Candidates", UsersThree],
        ["/interviews", "Interviews", CalendarBlank],
        ["/notifications", "Notifications", Bell],
        ["/referrals", "Referrals", Gift],
        ["/testimonials", "Testimonials", ChatCircleDots],
        ["/analytics", "Analytics", ChartBar],
        ["/performance", "Performance", ChartLineUp],
        ["/reports", "Reports", ChartPieSlice],
        ["/users", "Team", ShieldCheck],
        ["/billing", "Billing", CreditCard],
        ["/settings", "Settings", Gear],
    ],
    recruiter: [
        ["/dashboard", "Dashboard", SquaresFour],
        ["/clients", "Clients", Buildings],
        ["/requirements", "Requirements", Briefcase],
        ["/candidates", "Candidates", UsersThree],
        ["/interviews", "Interviews", CalendarBlank],
        ["/notifications", "Notifications", Bell],
        ["/analytics", "Analytics", ChartBar],
        ["/performance", "Performance", ChartLineUp],
    ],
    hiring_manager: [
        ["/dashboard", "Dashboard", SquaresFour],
        ["/clients", "Clients", Buildings],
        ["/requirements", "Requirements", Briefcase],
        ["/candidates", "Candidates", UsersThree],
        ["/interviews", "Interviews", CalendarBlank],
        ["/notifications", "Notifications", Bell],
        ["/testimonials", "Testimonials", ChatCircleDots],
        ["/analytics", "Analytics", ChartBar],
        ["/performance", "Performance", ChartLineUp],
        ["/reports", "Reports", ChartPieSlice],
    ],
};

export default function CommandPalette({ open, onOpenChange }) {
    const nav = useNavigate();
    const { user } = useAuth();
    const [q, setQ] = useState("");
    const [results, setResults] = useState({ jobs: [], candidates: [], clients: [] });
    const [loading, setLoading] = useState(false);
    const timer = useRef(null);
    const seq = useRef(0);

    const navItems = useMemo(() => NAV_BY_ROLE[user?.role] || [], [user?.role]);
    const canWrite = user?.role === "admin";

    const close = useCallback(() => {
        onOpenChange(false);
        setQ("");
    }, [onOpenChange]);

    const go = useCallback((to) => {
        nav(to);
        close();
    }, [nav, close]);

    // Debounced search
    useEffect(() => {
        if (!open) return;
        const term = q.trim();
        if (!term) {
            setResults({ jobs: [], candidates: [], clients: [] });
            setLoading(false);
            return;
        }
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(async () => {
            const mine = ++seq.current;
            setLoading(true);
            try {
                const [jRes, cRes, clRes] = await Promise.allSettled([
                    api.get("/jobs", { params: { q: term, limit: 6 } }),
                    api.get("/candidates/search", { params: { q: term, limit: 6 } }),
                    api.get("/clients", { params: { q: term, limit: 6 } }),
                ]);
                if (mine !== seq.current) return;
                setResults({
                    jobs: jRes.status === "fulfilled"
                        ? (Array.isArray(jRes.value.data) ? jRes.value.data : jRes.value.data?.items || []).slice(0, 6)
                        : [],
                    candidates: cRes.status === "fulfilled"
                        ? (cRes.value.data?.items || cRes.value.data || []).slice(0, 6)
                        : [],
                    clients: clRes.status === "fulfilled"
                        ? (Array.isArray(clRes.value.data) ? clRes.value.data : clRes.value.data?.items || []).slice(0, 6)
                        : [],
                });
            } catch {
                if (mine !== seq.current) return;
                setResults({ jobs: [], candidates: [], clients: [] });
            } finally {
                if (mine === seq.current) setLoading(false);
            }
        }, 200);
        return () => clearTimeout(timer.current);
    }, [q, open]);

    // Filter nav items by q
    const filteredNav = useMemo(() => {
        const t = q.trim().toLowerCase();
        if (!t) return navItems;
        return navItems.filter(([, label]) => label.toLowerCase().includes(t));
    }, [navItems, q]);

    return (
        <CommandDialog
            open={open}
            onOpenChange={(v) => { if (!v) close(); else onOpenChange(true); }}
        >
            <CommandInput
                data-testid="command-palette-input"
                placeholder="Jump to a page, candidate, requirement or client…"
                value={q}
                onValueChange={setQ}
            />
            <CommandList data-testid="command-palette-list">
                <CommandEmpty>
                    {loading ? "Searching…" : "No matches. Try a different search."}
                </CommandEmpty>

                {/* Search results (only when q has text) */}
                {q.trim() && results.jobs.length > 0 && (
                    <CommandGroup heading="Requirements">
                        {results.jobs.map((j) => (
                            <CommandItem
                                key={`j-${j.id}`}
                                value={`job ${j.title} ${j.code || ""} ${j.location || ""}`}
                                onSelect={() => go(`/requirements/${j.id}`)}
                                data-testid={`command-job-${j.id}`}
                            >
                                <Briefcase weight="bold" />
                                <span className="font-semibold truncate">{j.title}</span>
                                {j.code && (
                                    <span className="ml-auto text-xs text-[#A3A3A3] font-mono">{j.code}</span>
                                )}
                            </CommandItem>
                        ))}
                    </CommandGroup>
                )}

                {q.trim() && results.candidates.length > 0 && (
                    <>
                        <CommandSeparator />
                        <CommandGroup heading="Candidates">
                            {results.candidates.map((c) => (
                                <CommandItem
                                    key={`c-${c.id}`}
                                    value={`cand ${c.name || ""} ${c.email || ""} ${c.current_title || ""}`}
                                    onSelect={() => go(`/candidates?open=${c.id}`)}
                                    data-testid={`command-cand-${c.id}`}
                                >
                                    <UsersThree weight="bold" />
                                    <div className="min-w-0 flex-1">
                                        <div className="font-semibold truncate">{c.name || c.email}</div>
                                        {c.current_title && (
                                            <div className="text-xs text-[#A3A3A3] truncate">{c.current_title}</div>
                                        )}
                                    </div>
                                    {c.stage && (
                                        <span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-[#525252]">{c.stage}</span>
                                    )}
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </>
                )}

                {q.trim() && results.clients.length > 0 && (
                    <>
                        <CommandSeparator />
                        <CommandGroup heading="Clients">
                            {results.clients.map((cl) => (
                                <CommandItem
                                    key={`cl-${cl.id}`}
                                    value={`client ${cl.name || ""}`}
                                    onSelect={() => go(`/clients?open=${cl.id}`)}
                                    data-testid={`command-client-${cl.id}`}
                                >
                                    <Buildings weight="bold" />
                                    <span className="font-semibold truncate">{cl.name}</span>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </>
                )}

                {(q.trim() && (results.jobs.length || results.candidates.length || results.clients.length)) ? (
                    <CommandSeparator />
                ) : null}

                {/* Quick actions */}
                <CommandGroup heading="Quick actions">
                    <CommandItem
                        value="search candidates"
                        onSelect={() => go(`/candidates${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ""}`)}
                        data-testid="command-quick-search-cand"
                    >
                        <MagnifyingGlass weight="bold" />
                        <span>Search all candidates{q.trim() ? ` for "${q.trim()}"` : ""}</span>
                        <ArrowRight size={14} weight="bold" className="ml-auto text-[#A3A3A3]" />
                    </CommandItem>
                    {canWrite && (
                        <CommandItem
                            value="new requirement job"
                            onSelect={() => go("/requirements?new=1")}
                            data-testid="command-new-req"
                        >
                            <Plus weight="bold" />
                            <span>New requirement</span>
                        </CommandItem>
                    )}
                </CommandGroup>

                <CommandSeparator />

                {/* Navigation */}
                <CommandGroup heading="Go to">
                    {filteredNav.map(([to, label, Icon]) => (
                        <CommandItem
                            key={to}
                            value={`nav ${label}`}
                            onSelect={() => go(to)}
                            data-testid={`command-nav-${to.slice(1)}`}
                        >
                            <Icon weight="bold" />
                            <span>{label}</span>
                            <span className="ml-auto text-[10px] font-mono text-[#A3A3A3]">{to}</span>
                        </CommandItem>
                    ))}
                </CommandGroup>
            </CommandList>
        </CommandDialog>
    );
}
